import React, { ReactElement, forwardRef } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import { theme } from "../../theme";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FdButton from "./Button/FdButton";
import FdTypography from "./Typography/FdTypography";

export enum actionDialogType {
  REGULAR = "regular",
  WARNING = "warning",
}

export interface Props {
  open: boolean;
  title?: string;
  onConfirm?: Function;
  content?: string | ReactElement;
  icon?: React.ReactElement;
  confirmLabel?: string;
  onCancel?: Function;
  cancelLabel?: string;
  onClose?: Function;
  type?: actionDialogType;
  isLoading?: boolean;
  maxWidth?: false | "xs" | "sm" | "md" | "lg" | "xl";
  fullWidth?: boolean;
  disablePortal?: boolean; // The children will be under the DOM hierarchy of the parent component.
  dialogSize?: string;
  isCloseIcon?: boolean;
  contentPadding?: boolean;
  isDialogAction?: boolean;
}

const FdDialog = (
  {
    open,
    title,
    content,
    icon,
    onConfirm,
    confirmLabel = "Yes",
    onCancel,
    cancelLabel = "No",
    onClose,
    type = actionDialogType.REGULAR,
    isLoading,
    maxWidth,
    fullWidth,
    disablePortal,
    dialogSize,
    isCloseIcon,
    contentPadding,
    isDialogAction,
  }: Props,
  ref: any
) => {
  const handleConfirmClick = () => {
    if (typeof onConfirm === "function") {
      onConfirm();
    }
  };
  const handleCancelClick = () => {
    if (typeof onCancel === "function") {
      onCancel();
    }
  };
  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose();
    }
  };

  return (
    <Dialog
      onClose={() => {
        !isLoading && handleClose();
      }}
      aria-labelledby="dialog-title"
      open={open}
      ref={ref}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      disablePortal={disablePortal}
      sx={{
        textAlign: "center",
        margin: "auto",
        "& .MuiDialog-paper": {
          width: dialogSize,
          maxWidth: dialogSize,
          pt: 2,
        },
      }}
    >
      {
        isCloseIcon && (
          onClose && !isLoading && (
            <IconButton
              aria-label="close"
              sx={{
                position: "absolute",
                right: "8px",
                top: "8px",
              }}
              onClick={handleClose}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          )
        )
      }
    
      <Box>
        {icon &&
          React.cloneElement(icon, {
            sx: {
              marginBottom: "15px",
            },
            color:
              type === actionDialogType.REGULAR
                ? theme.palette.primary.main
                : theme.palette.error.main,
          })}
        {title && (
          <DialogTitle sx={{ fontWeight: "500", fontSize: "20px" }}>
            <FdTypography
              label={title || ""}
              variant="h2"
              component="h2"
              sx={{ marginBottom: "15px" }}
            >
              
            </FdTypography>
          </DialogTitle>
        )}

        <DialogContent sx={{ padding: `${!contentPadding && "0"}` }}>
          <Box>{content}</Box>
        </DialogContent>

        <DialogActions
          sx={{
            paddingBottom: `${!isDialogAction && "4"}`,
            px: 5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& .MuiButtonBase-root": { mx: 1 },
          }}
        >
          {!isLoading && (
            <Box>
              {onConfirm && (
                <FdButton
                  size="medium"
                  onClick={handleConfirmClick}
                  sx={{
                    marginTop: "20px",
                    marginBottom: "10px",
                    backgroundColor:
                      type === actionDialogType.REGULAR
                        ? theme.palette.primary.main
                        : theme.palette.error.main,
                  }}
                  ButtonName={confirmLabel}
                />
              )}
              {onCancel && (
                <FdButton
                  size="medium"
                  onClick={handleCancelClick}
                  sx={{
                    marginTop: "20px",
                    marginBottom: "10px",
                    marginLeft: "15px",
                    color:
                      type === actionDialogType.REGULAR
                        ? theme.palette.primary.main
                        : theme.palette.error.main,
                  }}
                  ButtonName={cancelLabel}
                />
              )}
            </Box>
          )}
          {isLoading === true && (
            <CircularProgress sx={{ marginTop: "20px" }} />
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default forwardRef(FdDialog);
